import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/vigorbuy';

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navLinks = [
    { label: 'Home', href: '/', isRoute: true },
    { label: 'Shopping Guide', href: '/vigorbuy-sheet-seo-guide', isRoute: true },
    { label: 'Resources', href: '#resources' },
    { label: 'Safety', href: '#about' },
  ];

  const linkClasses = isScrolled
    ? 'text-foreground-700 hover:text-primary-500'
    : 'text-background-50/90 hover:text-background-50';

  const logoTextClasses = isScrolled
    ? 'text-foreground-950'
    : 'text-background-50';

  const logoIconBg = isScrolled
    ? 'bg-primary-500'
    : 'bg-background-50/20 backdrop-blur-sm';

  const logoIconColor = isScrolled
    ? 'text-background-50'
    : 'text-background-50';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 h-20 flex items-center transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors duration-300 ${logoIconBg}`}>
            <i className={`ri-file-list-3-line text-xl ${logoIconColor}`}></i>
          </div>
          <span className={`text-xl font-heading font-bold tracking-tight transition-colors duration-300 ${logoTextClasses}`}>
            VigorBuy
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.isRoute ? (
              <Link
                key={link.label}
                to={link.href}
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${linkClasses}`}
              >
                {link.label}
              </Link>
            ) : (
              <a
                key={link.label}
                href={link.href}
                className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${linkClasses}`}
              >
                {link.label}
              </a>
            )
          )}

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${linkClasses}`}
            >
              Categories
              <i
                className={`ri-arrow-drop-down-line text-base transition-transform duration-200 ${
                  isDropdownOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </button>

            {isDropdownOpen && (
              <div className="absolute top-full right-0 mt-3 w-72 bg-background-50 rounded-lg py-2 max-h-[500px] overflow-y-auto shadow-lg border border-background-200">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.link}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 transition-colors duration-150 whitespace-nowrap cursor-pointer"
                    onClick={() => setIsDropdownOpen(false)}
                  >
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      className="w-10 h-10 rounded-md object-cover flex-shrink-0"
                    />
                    <span>{cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="ml-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            Buy It Now
          </a>
        </div>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${isScrolled ? 'text-foreground-950' : 'text-background-50'}`}
        >
          <i
            className={`text-2xl ${
              isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
            }`}
          ></i>
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden absolute top-20 left-0 right-0 bg-background-50 border-t border-background-200 py-4 px-6 max-h-[80vh] overflow-y-auto shadow-lg">
          <Link
            to="/"
            className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <i className="ri-home-4-line text-lg w-6 h-6 flex items-center justify-center"></i>
            Home
          </Link>
          <Link
            to="/vigorbuy-sheet-seo-guide"
            className="flex items-center gap-3 py-3 text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <i className="ri-book-open-line text-lg w-6 h-6 flex items-center justify-center"></i>
            Shopping Guide
          </Link>
          <a
            href="#resources"
            className="flex items-center gap-3 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <i className="ri-stack-line text-lg w-6 h-6 flex items-center justify-center"></i>
            Resources
          </a>
          <a
            href="#about"
            className="flex items-center gap-3 py-3 text-sm font-medium text-foreground-700 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <i className="ri-shield-check-line text-lg w-6 h-6 flex items-center justify-center"></i>
            Safety
          </a>

          <div className="border-t border-background-200 my-3 pt-3">
            <button
              onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
              className="w-full flex items-center justify-between py-3 text-sm font-semibold text-foreground-900 whitespace-nowrap cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-lg w-6 h-6 flex items-center justify-center"></i>
                Categories (14)
              </span>
              <i
                className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                  isMobileCatOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </button>
            {isMobileCatOpen && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.link}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 p-2 rounded-md hover:bg-background-100 transition-colors duration-150 cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <img
                      src={cat.imageUrl}
                      alt={cat.name}
                      className="w-8 h-8 rounded object-cover flex-shrink-0"
                    />
                    <span className="text-xs text-foreground-700 leading-tight">
                      {cat.name}
                    </span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="block mt-4 text-center px-5 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-shopping-bag-3-line mr-2"></i>
            Buy It Now
          </a>
        </div>
      )}
    </nav>
  );
}